import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import throttle from 'lodash-es/throttle.js';
import getRemBase from 'propel/getRemBase';
import { checkElementInView } from 'propel/checkElementInView';
import { showWithFade, hideWithFade } from 'propel/fadeToggle';

const propelInit = function() {
const openEvent = new Event('propel-nav-opened');
const closeEvent = new Event('propel-nav-closed');
const searchOpenEvent = new Event('propel-nav-search-opened');
const searchCloseEvent = new Event('propel-nav-search-closed');

const OVERLAY_CLASSES = {
	displayClass: 'propel-nav-overlay-displayed',
	openClass: 'propel-nav-overlay-open',
};

const MENU_CLASSES = {
	displayClass: 'block-nav-header__menu--visible',
	openClass: 'block-nav-header__menu--open',
};

const SEARCH_CLASSES = {
	displayClass: 'block-nav-header__search-form-container--visible',
	openClass: 'block-nav-header__search-form-container--open',
};

const SEARCH_DROPDOWN_CLASSES = {
	displayClass: 'block-nav-header__search-dropdown--visible',
	openClass: 'block-nav-header__search-dropdown--open',
};

const scrollableElements = document.querySelectorAll(
	'.block-nav-header__menu, .block-nav-header.is-style-dropdown-full-bleed .block-nav-submenu'
);

const navBlocks = document.querySelectorAll('.block-nav-header');
const navHamburgers = document.querySelectorAll('.block-nav-header__hamburger');
const navBlockDefaultColorModes = new Map();

navBlocks.forEach((navBlock) => {
	navBlockDefaultColorModes.set(
		navBlock,
		navBlock.getAttribute('data-color-mode')
	);
});

let isPageScrolled = window.scrollY > 0;

const updateNavColorMode = () => {
	navBlocks.forEach((navBlock) => {
		const defaultColorMode = navBlockDefaultColorModes.get(navBlock);

		if ('3' !== defaultColorMode) {
			return;
		}

		const isMenuOpen = navBlock.classList.contains('active');

		const isSearchOpen = !!navBlock.querySelector(
			`.block-nav-header__search-form-container.${SEARCH_CLASSES.openClass}, .block-nav-header__search-dropdown.${SEARCH_DROPDOWN_CLASSES.openClass}`
		);

		navBlock.setAttribute(
			'data-color-mode',
			isPageScrolled || isMenuOpen || isSearchOpen ? '1' : '3'
		);
	});
};

const observePageScroll = () => {
	const hasTransparentNav = Array.from(
		navBlockDefaultColorModes.values()
	).includes('3');

	if (!hasTransparentNav) {
		return;
	}

	const scrollSentinel = document.createElement('div');
	scrollSentinel.className = 'block-nav-header__scroll-sentinel';
	scrollSentinel.setAttribute('aria-hidden', 'true');
	document.body.append(scrollSentinel);

	checkElementInView(
		scrollSentinel,
		() => {
			isPageScrolled = false;
			updateNavColorMode();
		},
		() => {
			isPageScrolled = true;
			updateNavColorMode();
		}
	);
};

const navSearchButtons = document.querySelectorAll(
	'.block-nav-header__search-button'
);

const mobileNavQuery = window.matchMedia('(max-width: 1199.98px)');

const updateBodyNavState = () => {
	const isSubmenuOpen = !!document.querySelector('.block-nav-link.active');

	const isDrawerOpen = !!document.querySelector(
		'.block-nav-header__hamburger.active'
	);

	const isSearchDropdownOpen = !!document.querySelector(
		`.block-nav-header__search-dropdown.${SEARCH_DROPDOWN_CLASSES.openClass}`
	);

	const isNavActive = isSubmenuOpen || isDrawerOpen || isSearchDropdownOpen;

	const isOverlayOpen = document.body.classList.contains(
		OVERLAY_CLASSES.openClass
	);

	if (isNavActive && !isOverlayOpen) {
		showWithFade(document.body, OVERLAY_CLASSES);
	} else if (!isNavActive && isOverlayOpen) {
		hideWithFade(document.body, OVERLAY_CLASSES);
	}

	document.body.classList.toggle(
		'propel-nav-submenu-mobile-active',
		isSubmenuOpen && mobileNavQuery.matches
	);

	updateNavColorMode();
};

const toggleNavHamburger = (e) => {
	const currentNavHamburger = e?.currentTarget ?? e;
	const currentNav = currentNavHamburger?.closest('.block-nav-header');
	const currentNavMenu = currentNav?.querySelector('.block-nav-header__menu');
	currentNavHamburger.classList.toggle('active');

	if (currentNavHamburger.classList.contains('active')) {
		window.dispatchEvent(openEvent);
		setNavMenuActiveClass(currentNavHamburger, true);
		toggleSidebarFirstSubmenu(currentNav, true);
		currentNavHamburger.setAttribute('aria-expanded', true);

		showWithFade(
			currentNavMenu,
			MENU_CLASSES,
			setFirstLevelButtonBlockOffsets
		);

		if (
			window.innerWidth < 992 ||
			currentNav.classList.contains('is-style-dropdown-sidebar')
		) {
			lockScroll();
		}
	} else {
		window.dispatchEvent(closeEvent);
		clearAllBodyScrollLocks();
		setNavMenuActiveClass(currentNavHamburger, false);
		toggleSidebarFirstSubmenu(currentNav, false);
		currentNavHamburger.setAttribute('aria-expanded', false);
		hideWithFade(currentNavMenu, MENU_CLASSES);
	}

	updateBodyNavState();
};

const toggleSidebarFirstSubmenu = (currentNav, isOpening) => {
	const isDropdownSidebar = currentNav?.classList.contains(
		'is-style-dropdown-sidebar'
	);

	const firstNavLink = currentNav?.querySelector(
		'.block-nav-header__menu > .block-nav-link'
	);

	if (!isDropdownSidebar || mobileNavQuery.matches) {
		return;
	}

	firstNavLink?.classList.toggle('active', isOpening);

	const firstNavLinkButton = firstNavLink?.querySelector(
		'.block-nav-link__button--with-submenu'
	);

	const eventName = isOpening
		? 'propel-nav-submenu-opened'
		: 'propel-nav-submenu-closed';

	window.dispatchEvent(
		new CustomEvent(eventName, {
			detail: { currentElement: firstNavLinkButton ?? firstNavLink },
		})
	);
};

const submenuClosed = () => {
	if (window.innerWidth >= 992) {
		clearAllBodyScrollLocks();
	}
};

const setNavLinkCount = () => {
	if (0 === navBlocks.length) {
		return;
	}

	navBlocks.forEach((navBlock) => {
		if (!navBlock.classList.contains('is-style-dropdown-sidebar')) {
			return;
		}

		const menu = navBlock.querySelector('.block-nav-header__menu');

		if (!menu) {
			return;
		}

		const navLinkCount =
			menu.querySelectorAll(':scope > .block-nav-link')?.length ?? 0;

		const navButtonCount =
			menu.querySelectorAll(':scope > .wp-block-button')?.length ?? 0;

		menu.style.setProperty('--navLinkCount', navLinkCount);
		menu.style.setProperty('--navButtonCount', navButtonCount);
	});
};

const setFirstLevelButtonBlockOffsets = () => {
	if (0 === navBlocks.length) {
		return;
	}

	// Read every block's height first, then write offsets in a second pass — interleaving the two forces a layout recalc per block.
	const navBlockData = Array.from(navBlocks, (navBlock) => {
		const firstLevelButtonBlocks = navBlock.querySelectorAll(
			'.block-nav-header__menu > .wp-block-button'
		);

		const heights = Array.from(
			firstLevelButtonBlocks,
			(firstLevelButtonBlock) => firstLevelButtonBlock.offsetHeight
		);

		return { navBlock, firstLevelButtonBlocks, heights };
	});

	navBlockData.forEach(({ navBlock, firstLevelButtonBlocks, heights }) => {
		if (0 === firstLevelButtonBlocks.length) {
			return;
		}

		let totalOffset = 0;

		for (let i = firstLevelButtonBlocks.length - 1; i >= 0; i--) {
			firstLevelButtonBlocks[i].style.setProperty(
				'--buttonOffset',
				`${totalOffset / getRemBase()}rem`
			);

			totalOffset += heights[i];
		}

		navBlock.style.setProperty(
			'--totalButtonOffset',
			`${totalOffset / getRemBase()}rem`
		);
	});
};

const isAnyNavOpen = () =>
	!!document.querySelector('.block-nav-header__hamburger.active');

const setNavMenuActiveClass = (element, navActive = null) => {
	const currentElement = element?.detail?.currentElement ?? element;

	if (!currentElement) {
		return;
	}

	const currentNavBlock = currentElement.closest('.block-nav-header');

	if (!currentNavBlock) {
		return;
	}

	if (null === navActive) {
		if (
			currentElement.classList.contains('active') ||
			currentElement.parentElement.classList.contains('active') ||
			currentNavBlock.querySelector('.block-nav-header__hamburger.active')
		) {
			navActive = true;
		} else {
			navActive = false;
		}
	}

	if (true === navActive) {
		currentNavBlock.classList.add('active');
		document.addEventListener('keydown', clickOffMenu);
		document.addEventListener('click', clickOffMenu);
	} else {
		currentNavBlock.classList.remove('active');
		document.removeEventListener('keydown', clickOffMenu);
		document.removeEventListener('click', clickOffMenu);
	}

	navBlocks.forEach((navBlock) => {
		if (navBlock !== currentNavBlock) {
			navBlock.classList.remove('active');
		}
	});

	updateBodyNavState();
};

const lockScroll = () => {
	if (scrollableElements.length) {
		scrollableElements.forEach(function (scrollableElement) {
			const storedRequestAnimationFrame = window.requestAnimationFrame;
			window.requestAnimationFrame = () => 42;

			disableBodyScroll(scrollableElement, {
				reserveScrollBarGap: true,
			});

			window.requestAnimationFrame = storedRequestAnimationFrame;
		});
	}
};

const clickOffMenu = (e) => {
	if (0 === navHamburgers.length) {
		return;
	}

	let closestMenu;
	let closestHamburger;

	if (e.key === undefined && e.target) {
		closestMenu = e.target.closest('.block-nav-header__menu');
		closestHamburger = e.target.closest('.block-nav-header__hamburger');
	}

	if (e.key && e.key !== 'Escape') {
		return;
	}

	if (
		closestMenu?.contains(e.target) ||
		closestHamburger?.contains(e.target)
	) {
		return;
	}

	navHamburgers.forEach((navHamburger) => {
		if (navHamburger.classList.contains('active')) {
			toggleNavHamburger(navHamburger);
		}
	});

	window.dispatchEvent(closeEvent);
};

const getSearchPanelClasses = (searchPanel) =>
	searchPanel?.classList.contains('block-nav-header__search-dropdown')
		? SEARCH_DROPDOWN_CLASSES
		: SEARCH_CLASSES;

const toggleSearch = (e) => {
	const currentSearchButton = e.currentTarget;
	const currentNavBlock = currentSearchButton.closest('.block-nav-header');

	if (!currentNavBlock) {
		return;
	}

	const searchPanel = currentNavBlock.querySelector(
		'.block-nav-header__search-form-container, .block-nav-header__search-dropdown'
	);

	if (!searchPanel) {
		return;
	}

	const isDropdownSearch = searchPanel.classList.contains(
		'block-nav-header__search-dropdown'
	);

	const panelClasses = getSearchPanelClasses(searchPanel);
	const isOpen = searchPanel.classList.contains(panelClasses.openClass);

	if (isOpen) {
		hideWithFade(searchPanel, panelClasses);
		currentSearchButton.setAttribute('aria-expanded', false);

		if (isDropdownSearch) {
			currentSearchButton.classList.remove('active');
		}

		window.dispatchEvent(searchCloseEvent);
		document.removeEventListener('keydown', clickOffSearch);
		document.removeEventListener('click', clickOffSearch);
		updateBodyNavState();

		return;
	}

	showWithFade(searchPanel, panelClasses);

	navBlocks.forEach((navBlock) => {
		if (navBlock === currentNavBlock) {
			return;
		}

		const otherPanel = navBlock.querySelector(
			'.block-nav-header__search-form-container, .block-nav-header__search-dropdown'
		);

		const otherPanelClasses = getSearchPanelClasses(otherPanel);

		if (otherPanel?.classList.contains(otherPanelClasses.openClass)) {
			hideWithFade(otherPanel, otherPanelClasses);
		}

		const openElements = navBlock.querySelectorAll(
			'.block-nav-header__search-button[aria-expanded="true"]'
		);

		openElements.forEach((openElement) => {
			openElement.setAttribute('aria-expanded', false);
			openElement.classList.remove('active');
		});
	});

	currentSearchButton.setAttribute('aria-expanded', true);

	if (isDropdownSearch) {
		currentSearchButton.classList.add('active');
	}

	window.dispatchEvent(searchOpenEvent);
	document.addEventListener('keydown', clickOffSearch);
	document.addEventListener('click', clickOffSearch);
	updateBodyNavState();

	const searchInput = searchPanel.querySelector('input[type="search"]');

	searchInput?.focus();
};

const closeSearch = (e) => {
	if (0 === navSearchButtons.length) {
		return;
	}

	document
		.querySelectorAll(
			`.block-nav-header__search-form-container.${SEARCH_CLASSES.openClass}`
		)
		.forEach((formContainer) => {
			hideWithFade(formContainer, SEARCH_CLASSES);
		});

	document
		.querySelectorAll(
			`.block-nav-header__search-dropdown.${SEARCH_DROPDOWN_CLASSES.openClass}`
		)
		.forEach((searchDropdown) => {
			hideWithFade(searchDropdown, SEARCH_DROPDOWN_CLASSES);
		});

	navSearchButtons.forEach((navSearchButton) => {
		navSearchButton.setAttribute('aria-expanded', false);
		navSearchButton.classList.remove('active');
	});

	const currentNavBlock = e?.currentTarget?.closest?.('.block-nav-header');

	if (currentNavBlock) {
		currentNavBlock
			.querySelector('.block-nav-header__search-button')
			?.focus();
	}

	window.dispatchEvent(searchCloseEvent);
	document.removeEventListener('keydown', clickOffSearch);
	document.removeEventListener('click', clickOffSearch);
	updateBodyNavState();
};

const clickOffSearch = (e) => {
	if (0 === navSearchButtons.length) {
		return;
	}

	if (e.key && e.key !== 'Escape') {
		return;
	}

	let closestSearchPanel;
	let closestSearchButton;

	if (e.key === undefined && e.target) {
		closestSearchPanel = e.target.closest(
			'.block-nav-header__search-form-container, .block-nav-header__search-dropdown'
		);

		closestSearchButton = e.target.closest(
			'.block-nav-header__search-button'
		);
	}

	if (
		closestSearchPanel?.contains(e.target) ||
		closestSearchButton?.contains(e.target)
	) {
		return;
	}

	closeSearch();
};

navHamburgers.forEach((navHamburger) => {
	navHamburger.addEventListener('click', toggleNavHamburger);
});

navSearchButtons.forEach((navSearchButton) => {
	navSearchButton.addEventListener('click', toggleSearch);
});

const navSearchCloseButtons = document.querySelectorAll(
	'.block-nav-header__search-close'
);

navSearchCloseButtons.forEach((navSearchCloseButton) => {
	navSearchCloseButton.addEventListener('click', closeSearch);
});

setNavLinkCount();
updateNavColorMode();
observePageScroll();

navBlocks.forEach((navBlock) => {
	navBlock.classList.add('block-nav-header--initialized');
});

window.addEventListener('propel-nav-submenu-closed', submenuClosed);
window.addEventListener('propel-nav-submenu-closed', setNavMenuActiveClass);
window.addEventListener('propel-nav-submenu-opened', setNavMenuActiveClass);
window.addEventListener('propel-nav-opened', closeSearch);
window.addEventListener('propel-nav-submenu-opened', closeSearch);

window.addEventListener(
	'resize',
	throttle(() => {
		if (isAnyNavOpen()) {
			setFirstLevelButtonBlockOffsets();
		}
	}, 100)
);

window.addEventListener('propel-rem-base-updated', () => {
	if (isAnyNavOpen()) {
		setFirstLevelButtonBlockOffsets();
	}
});

const closeOpenNavsOnBreakpointChange = () => {
	navHamburgers.forEach((navHamburger) => {
		if (navHamburger.classList.contains('active')) {
			toggleNavHamburger(navHamburger);
		}
	});

	closeSearch();
	window.dispatchEvent(closeEvent);
	updateBodyNavState();
};

mobileNavQuery.addEventListener('change', closeOpenNavsOnBreakpointChange);

};
propelInit();
window.propel = window.propel || {};
window.propel.blocks = window.propel.blocks || {};
window.propel.blocks['nav-header'] = window.propel.blocks['nav-header'] || {};
window.propel.blocks['nav-header'].script = propelInit;
